export const SEARCH_NAVIGATION_SOURCE_ATTRIBUTE =
  "data-search-navigation-source";
export const BLOG_SEARCH_RESULT_ORIGIN_DATASET_KEY = "blogSearchResultOrigin";
export const BLOG_SEARCH_PENDING_RESULT_ORIGIN_DATASET_KEY =
  "blogSearchPendingResultOrigin";
export const BLOG_SEARCH_PENDING_RESULT_HREF_DATASET_KEY =
  "blogSearchPendingResultHref";
const BLOG_SEARCH_PENDING_RESULT_CREATED_AT_DATASET_KEY =
  "blogSearchPendingResultCreatedAt";
const BLOG_SEARCH_PENDING_ATTEMPT_ID_DATASET_KEY = "blogSearchPendingAttemptId";
const BLOG_SEARCH_PENDING_INPUT_MODE_DATASET_KEY = "blogSearchPendingInputMode";
export const BLOG_SEARCH_PENDING_RESULT_NAVIGATION_STORAGE_KEY =
  "blogSearchPendingResultNavigation";
const LEGACY_BLOG_SEARCH_PENDING_RESULT_ORIGIN_STORAGE_KEY =
  "blogSearchPendingResultOrigin";
const PENDING_SEARCH_NAVIGATION_MAX_AGE_MS = 30_000;

export type SearchNavigationSource =
  | "initial"
  | "home_submit"
  | "home_suggestion"
  | "home_rewrite"
  | "home_view_all"
  | "submit"
  | "suggestion"
  | "rewrite"
  | "category_pill"
  | "did_you_mean"
  | "intent_facet"
  | "confidence_recovery"
  | "zero_results_recovery"
  | "live_debounce"
  | "navigation";

export function isSearchNavigationSource(
  value: string | undefined
): value is SearchNavigationSource {
  return (
    value === "initial" ||
    value === "home_submit" ||
    value === "home_suggestion" ||
    value === "home_rewrite" ||
    value === "home_view_all" ||
    value === "submit" ||
    value === "suggestion" ||
    value === "rewrite" ||
    value === "category_pill" ||
    value === "did_you_mean" ||
    value === "intent_facet" ||
    value === "confidence_recovery" ||
    value === "zero_results_recovery" ||
    value === "live_debounce" ||
    value === "navigation"
  );
}

export type PendingBlogSearchNavigation = {
  href: string;
  source: SearchNavigationSource;
  createdAtMs?: number;
  searchAttemptId?: string;
  searchInputMode?: string;
};

function getRootDataset(): DOMStringMap | null {
  if (typeof document === "undefined") return null;
  return document.documentElement.dataset;
}

export function normalizeBlogSearchNavigationHref(
  href: string | null | undefined
): string | undefined {
  const trimmed = href?.trim();
  if (!trimmed) return undefined;

  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://eclat.local";
    const resolved = new URL(trimmed, base);
    resolved.searchParams.sort();
    const search = resolved.searchParams.toString();
    return search ? `${resolved.pathname}?${search}` : resolved.pathname;
  } catch {
    return undefined;
  }
}

function readPendingNavigationFromStorage():
  | PendingBlogSearchNavigation
  | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.sessionStorage.getItem(
      BLOG_SEARCH_PENDING_RESULT_NAVIGATION_STORAGE_KEY
    );
    if (!raw) return undefined;

    const parsed = JSON.parse(raw) as {
      href?: unknown;
      source?: unknown;
      createdAtMs?: unknown;
      searchAttemptId?: unknown;
      searchInputMode?: unknown;
    };
    const href =
      typeof parsed.href === "string"
        ? normalizeBlogSearchNavigationHref(parsed.href)
        : undefined;
    const source =
      typeof parsed.source === "string" &&
      isSearchNavigationSource(parsed.source)
        ? parsed.source
        : undefined;
    const createdAtMs =
      typeof parsed.createdAtMs === "number" &&
      Number.isFinite(parsed.createdAtMs)
        ? parsed.createdAtMs
        : undefined;
    const searchAttemptId =
      typeof parsed.searchAttemptId === "string" &&
      parsed.searchAttemptId.trim().length > 0
        ? parsed.searchAttemptId.trim()
        : undefined;
    const searchInputMode =
      typeof parsed.searchInputMode === "string" &&
      parsed.searchInputMode.trim().length > 0
        ? parsed.searchInputMode.trim()
        : undefined;

    return href && source
      ? {
          href,
          searchAttemptId,
          searchInputMode,
          createdAtMs,
          source,
        }
      : undefined;
  } catch {
    return undefined;
  }
}

function writePendingNavigationToStorage({
  createdAtMs,
  href,
  searchAttemptId,
  searchInputMode,
  source,
}: PendingBlogSearchNavigation) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      BLOG_SEARCH_PENDING_RESULT_NAVIGATION_STORAGE_KEY,
      JSON.stringify({
        createdAtMs,
        href,
        searchAttemptId,
        searchInputMode,
        source,
      })
    );
  } catch {
    // Ignore storage failures; same-document dataset propagation still works.
  }
}

function clearPendingNavigationFromStorage() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(
      BLOG_SEARCH_PENDING_RESULT_NAVIGATION_STORAGE_KEY
    );
    window.sessionStorage.removeItem(
      LEGACY_BLOG_SEARCH_PENDING_RESULT_ORIGIN_STORAGE_KEY
    );
  } catch {
    // Ignore storage failures; this is an analytics/UX hint, not critical state.
  }
}

function clearPendingNavigationFromDataset(dataset: DOMStringMap | null) {
  if (!dataset) return;
  delete dataset[BLOG_SEARCH_PENDING_RESULT_ORIGIN_DATASET_KEY];
  delete dataset[BLOG_SEARCH_PENDING_RESULT_HREF_DATASET_KEY];
  delete dataset[BLOG_SEARCH_PENDING_RESULT_CREATED_AT_DATASET_KEY];
  delete dataset[BLOG_SEARCH_PENDING_ATTEMPT_ID_DATASET_KEY];
  delete dataset[BLOG_SEARCH_PENDING_INPUT_MODE_DATASET_KEY];
}

function isPendingNavigationExpired(createdAtMs: number | undefined): boolean {
  if (createdAtMs === undefined) return false;
  return Date.now() - createdAtMs > PENDING_SEARCH_NAVIGATION_MAX_AGE_MS;
}

export function clearPendingBlogSearchResultOrigin() {
  const dataset = getRootDataset();
  clearPendingNavigationFromDataset(dataset);
  clearPendingNavigationFromStorage();
}

export function setPendingBlogSearchResultOrigin(
  navigation: PendingBlogSearchNavigation
) {
  const href = normalizeBlogSearchNavigationHref(navigation.href);
  if (!href) return;
  const createdAtMs = navigation.createdAtMs ?? Date.now();

  const dataset = getRootDataset();
  if (dataset) {
    dataset[BLOG_SEARCH_PENDING_RESULT_ORIGIN_DATASET_KEY] = navigation.source;
    dataset[BLOG_SEARCH_PENDING_RESULT_HREF_DATASET_KEY] = href;
    dataset[BLOG_SEARCH_PENDING_RESULT_CREATED_AT_DATASET_KEY] =
      String(createdAtMs);
    if (navigation.searchAttemptId) {
      dataset[BLOG_SEARCH_PENDING_ATTEMPT_ID_DATASET_KEY] =
        navigation.searchAttemptId;
    } else {
      delete dataset[BLOG_SEARCH_PENDING_ATTEMPT_ID_DATASET_KEY];
    }
    if (navigation.searchInputMode) {
      dataset[BLOG_SEARCH_PENDING_INPUT_MODE_DATASET_KEY] =
        navigation.searchInputMode;
    } else {
      delete dataset[BLOG_SEARCH_PENDING_INPUT_MODE_DATASET_KEY];
    }
  }
  writePendingNavigationToStorage({
    createdAtMs,
    href,
    searchAttemptId: navigation.searchAttemptId,
    searchInputMode: navigation.searchInputMode,
    source: navigation.source,
  });
}

export function consumePendingBlogSearchResultOrigin():
  | SearchNavigationSource
  | undefined {
  return consumePendingBlogSearchResultOriginForHref();
}

export function consumePendingBlogSearchResultOriginForHref(
  expectedHref?: string
): SearchNavigationSource | undefined {
  return consumePendingBlogSearchNavigationForHref(expectedHref)?.source;
}

export function consumePendingBlogSearchNavigationForHref(
  expectedHref?: string
): PendingBlogSearchNavigation | undefined {
  const dataset = getRootDataset();
  const datasetValue = dataset?.[BLOG_SEARCH_PENDING_RESULT_ORIGIN_DATASET_KEY];
  const datasetHref = normalizeBlogSearchNavigationHref(
    dataset?.[BLOG_SEARCH_PENDING_RESULT_HREF_DATASET_KEY]
  );
  const datasetCreatedAtRaw =
    dataset?.[BLOG_SEARCH_PENDING_RESULT_CREATED_AT_DATASET_KEY];
  const datasetCreatedAt =
    typeof datasetCreatedAtRaw === "string" &&
    datasetCreatedAtRaw.trim().length > 0
      ? Number(datasetCreatedAtRaw)
      : undefined;
  const datasetSearchAttemptId =
    dataset?.[BLOG_SEARCH_PENDING_ATTEMPT_ID_DATASET_KEY]?.trim() || undefined;
  const datasetSearchInputMode =
    dataset?.[BLOG_SEARCH_PENDING_INPUT_MODE_DATASET_KEY]?.trim() || undefined;
  const storageValue = readPendingNavigationFromStorage();
  const pendingNavigation =
    isSearchNavigationSource(datasetValue) && datasetHref
      ? {
          createdAtMs:
            typeof datasetCreatedAt === "number" &&
            Number.isFinite(datasetCreatedAt)
              ? datasetCreatedAt
              : undefined,
          href: datasetHref,
          searchAttemptId: datasetSearchAttemptId,
          searchInputMode: datasetSearchInputMode,
          source: datasetValue,
        }
      : storageValue;
  if (
    pendingNavigation &&
    isPendingNavigationExpired(pendingNavigation.createdAtMs)
  ) {
    clearPendingNavigationFromDataset(dataset);
    clearPendingNavigationFromStorage();
    return undefined;
  }
  const normalizedExpectedHref =
    normalizeBlogSearchNavigationHref(expectedHref);
  const matchesExpectedHref =
    pendingNavigation &&
    (!normalizedExpectedHref ||
      pendingNavigation.href === normalizedExpectedHref);
  if (matchesExpectedHref) {
    clearPendingNavigationFromDataset(dataset);
    clearPendingNavigationFromStorage();
    return pendingNavigation;
  }
  return undefined;
}

export function setBlogSearchResultOrigin(source: SearchNavigationSource) {
  const dataset = getRootDataset();
  if (!dataset) return;
  dataset[BLOG_SEARCH_RESULT_ORIGIN_DATASET_KEY] = source;
}

export function clearBlogSearchNavigationState() {
  const dataset = getRootDataset();
  if (dataset) {
    delete dataset[BLOG_SEARCH_RESULT_ORIGIN_DATASET_KEY];
  }
  clearPendingBlogSearchResultOrigin();
}
